/* ==========================================================================
   Reviews, updates, settings and feedback
   ========================================================================== */

import { useEffect, useRef, useState } from 'react';
import {
  Avatar, Card, Divider, Note, Pick, SectionHead, Seg, StarPicker, SectionHead as SH,
  Switch,
} from '../../components/ui';
import {
  CLEANER_RATING_DIMS, FEEDBACK_TOPICS, HOUSE_RATING_DIMS,
} from '../../data/catalog';
import { AVAILABILITY, BOOKINGS, NOTIFICATIONS } from '../../data/seed';
import { UI_LANGS } from '../../lib/i18n';
import { byId, cleaner, house, dimLabel } from '../../lib/domain';
import { ago, fmtDate } from '../../lib/time';
import { t, tf } from '../../lib/i18n';
import type { ScreenName } from '../../store';
import {
  FEEDBACK, cancelSubscription, commit, deleteAccount, loadHidden, markUpdatesRead, nav,
  redeemPromo, resumeSubscription, sendFeedback, setLang, setViewport, signOut,
  startSubscription, state, submitReview, toast, toggleHidden, unhidePerson, viewAs,
} from '../../store';
import { api } from '../../api';
import { PushNudge, PushSettings, TextsSettings } from '../../components/PushCard';

/* ---------------------------------------------------------------- review */

export function ReviewScreen() {
  const b = byId(BOOKINGS, state.params.id!)!;
  const side = state.role;
  const other = side === 'house' ? cleaner(b.cleanerId) : house(b.houseId);
  const otherName = side === 'house' ? cleaner(b.cleanerId).name : house(b.houseId).displayName;
  const dims = side === 'house' ? CLEANER_RATING_DIMS : HOUSE_RATING_DIMS;
  const d = state.reviewDraft;
  const set = (k: string, v: unknown) => { d[k] = v; commit(); };

  return (
    <div className="screen-pad">
      <Card>
        <div className="row top">
          <Avatar p={other} />
          <div className="col grow">
            <b className="name">{otherName}</b>
            <span className="sub">
              {fmtDate(b.date, { long: true })} · {b.hours} {t('hours')}
            </span>
          </div>
        </div>
      </Card>

      <Note warm>
        <b>Neither of you sees the other&apos;s review</b> until you have both written one, or
        14 days pass.
      </Note>

      <Card>
        <SectionHead title={t('h_overall')} />
        <StarPicker value={d.overall || 0} onPick={(n) => set('overall', n)} />
      </Card>

      {dims.map((dim) => (
        <Card key={dim.id}>
          <SectionHead title={dimLabel(dim)} />
          <StarPicker value={d[dim.id] || 0} onPick={(n) => set(dim.id, n)} />
        </Card>
      ))}

      <Card>
        <SectionHead title={t('h_inyourwords')} />
        <textarea
          className="textarea"
          placeholder={
            'What would the next ' + (side === 'house' ? 'family' : 'cleaner') + ' want to know?'
          }
          value={d.text || ''}
          onChange={(e) => set('text', e.target.value)}
        />
      </Card>

      {side === 'house' && (
        <Card>
          <SectionHead title={t('h_bookagain')} />
          <Seg
            options={[
              { id: 'yes', label: 'Yes' },
              { id: 'maybe', label: 'Maybe' },
              { id: 'no', label: 'No' },
            ]}
            value={d.again || ''}
            onChange={(v) => set('again', v)}
          />
        </Card>
      )}

      {!d.overall && <span className="hint">Pick a star rating to send this. The rest is optional.</span>}
      <button
        type="button"
        className="btn lg block"
        disabled={!d.overall}
        onClick={() => submitReview(b.id)}
      >
        {t('b_submit')}
      </button>
    </div>
  );
}

/* --------------------------------------------------------------- updates */

export function Notifications() {
  /* Opening this screen is reading them. Nothing here waits for a tap on
     each one: nobody taps an update to acknowledge it, they read the list and
     leave, and an app that keeps insisting the list is new after that is
     simply wrong -- which is what Oster found.

     Run once on the way in, before the first paint of the list, so the badge
     is gone by the time the screen is on-screen but the "New" chips stay put
     while it is being read. */
  const marked = useRef(false);
  useEffect(() => {
    if (marked.current) return;
    marked.current = true;
    markUpdatesRead();
  }, []);

  return (
    <div className="screen-pad">
      <PushNudge />
      {NOTIFICATIONS[state.role].map((n) => (
        <Card
          key={n.id}
          className={n.unread ? 'tint' : ''}
          onClick={() => {
            const parts = n.go.replace('#/', '').split('/');
            nav((parts[0] + '_' + parts[1]) as ScreenName, { id: parts[2] });
          }}
        >
          <div className="row top">
            <div className="col grow">
              <b style={{ fontSize: 14.5 }}>{n.text}</b>
              <span className="sub tight">{ago(n.at)}</span>
            </div>
            {n.unread && <span className="chip warm">New</span>}
          </div>
        </Card>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------- delete */

/** Deleting yourself. Two taps and a word typed, because it is not undone. */
function DeleteAccount() {
  const [open, setOpen] = useState(false);
  const [word, setWord] = useState('');
  /* Why they are going, if they want to say. Never required: the only gate
     on leaving is the word DELETE. Before this, the first home to leave did
     so three hours after joining and nobody could tell why. */
  const [reason, setReason] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const house = state.role === 'house';
  const REASONS: [string, string][] = [
    ['nobody-near', t(house ? 'dl_nocleaners' : 'dl_nohomes')],
    ['too-expensive', t('dl_expensive')],
    ['just-looking', t('dl_looking')],
    ['found-elsewhere', t(house ? 'dl_foundhelp' : 'dl_foundwork')],
    ['not-working', t('dl_broken')],
    ['other', t('dl_other')],
  ];
  return (
    <Card>
      <SH title={t('h_deleteaccount')} />
      {!open ? (
        <>
          <span className="sub">{t('n_deletehint')}</span>
          <button type="button" className="btn quiet block" onClick={() => setOpen(true)}>
            {t('b_deleteaccount')}
          </button>
        </>
      ) : (
        <>
          <span className="sub" style={{ fontWeight: 700 }}>{t('dl_why')}</span>
          <div className="chips">
            {REASONS.map(([id, label]) => (
              <Pick key={id} label={label} on={reason === id} onClick={() => setReason(reason === id ? null : id)} />
            ))}
          </div>
          <textarea
            id="delete-note"
            className="textarea"
            maxLength={500}
            placeholder={t('dl_note')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <Note warm>{t('n_deletewarn')}</Note>
          <input
            className="input mono"
            placeholder="DELETE"
            value={word}
            autoCapitalize="characters"
            onChange={(e) => setWord(e.target.value.toUpperCase())}
          />
          <div className="row" style={{ gap: 8 }}>
            <button type="button" className="btn quiet grow" onClick={() => { setOpen(false); setWord(''); }}>
              {t('b_cancel')}
            </button>
            <button
              type="button"
              className="btn danger grow"
              disabled={word !== 'DELETE' || state.busy}
              onClick={() => void deleteAccount(reason, note)}
            >
              {t('b_deleteforgood')}
            </button>
          </div>
        </>
      )}
    </Card>
  );
}

/* -------------------------------------------------------------- settings */

/** A code somebody was given, and the code they give away.
 *
 *  Together in one card on purpose. They are the same idea from two sides --
 *  a code buys free months -- and somebody who has just typed one in is
 *  exactly the person most likely to pass theirs on. */
function PromoAndSharing() {
  const [code, setCode] = useState('');
  const [share, setShare] = useState<{
    code: string; joined: number; monthsEarned: number;
    monthsLeft: number; perSignup: number;
  } | null>(null);

  useEffect(() => {
    let alive = true;
    api.shareState().then((s) => { if (alive) setShare(s); }).catch(() => { /* later */ });
    return () => { alive = false; };
  }, []);

  const link = share ? window.location.origin + '/?code=' + share.code : '';

  return (
    <Card>
      <SectionHead title="Codes" />

      {/* One box, two kinds of code, and they pay two different people. "It
          adds free months on top of whatever you already have" was true of a
          launch code and plainly false of a friend's — and a friend's is the
          one being shared, so the wrong half was the one people would read. */}
      <span className="hint">
        {t('tx_promo_hint')}
      </span>
      <div className="row" style={{ gap: 8 }}>
        <input
          className="input grow"
          placeholder="OZ4K7PQX"
          value={code}
          maxLength={24}
          autoCapitalize="characters"
          spellCheck={false}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
        />
        <button
          type="button"
          className="btn"
          disabled={state.busy || code.trim().length < 4}
          onClick={() => {
            void redeemPromo(code.trim()).then((ok) => {
              if (ok) {
                setCode('');
                api.shareState().then(setShare).catch(() => { /* later */ });
              }
            });
          }}
        >
          Add it
        </button>
      </div>

      {share && (
        <>
          <div className="hr" />
          <span className="hint">
            {share.perSignup === 1 ? t('tx_share_hint_1') : tf('tx_share_hint_n', { n: share.perSignup })}
          </span>
          <div className="row" style={{ gap: 8, alignItems: 'center' }}>
            <b style={{ fontSize: 20, letterSpacing: '.08em' }}>{share.code}</b>
            <button
              type="button"
              className="btn quiet sm"
              onClick={() => {
                const nav = window.navigator as Navigator & {
                  share?: (d: unknown) => Promise<void>;
                };
                const text = 'Come and find a cleaner on Ozeret — use my code '
                  + share.code + ' when you sign up.';
                if (nav.share) {
                  void nav.share({ title: 'Ozeret', text, url: link }).catch(() => { /* cancelled */ });
                } else {
                  void navigator.clipboard.writeText(text + ' ' + link)
                    .then(() => toast('Copied.'))
                    .catch(() => toast('Could not copy just now.'));
                }
              }}
            >
              Share it
            </button>
          </div>
          <span className="hint">
            {share.joined === 0
              ? 'Nobody has used it yet.'
              : share.joined + (share.joined === 1 ? ' person has' : ' people have')
                + ' joined on it, earning you '
                + share.monthsEarned + (share.monthsEarned === 1 ? ' month' : ' months') + '.'}
            {share.monthsLeft > 0
              ? ' You can earn ' + share.monthsLeft + ' more.'
              : ' That is the most anybody can earn this way.'}
          </span>
        </>
      )}
    </Card>
  );
}

export function Settings() {
  const isCleaner = state.role === 'cleaner';
  const av = isCleaner ? AVAILABILITY[state.cleanerId] : null;

  const b = state.billing;
  const until = (iso: string | null) => (iso ? fmtDate(iso.slice(0, 10), { long: true }) : '');

  return (
    <div className="screen-pad formgrid">
      {/* Phone notifications: on or off for this device, and which kinds. */}
      <PushSettings />
      {/* Texts about their own jobs: only ever on by their own tap. */}
      <TextsSettings />

      {/* What they are paying, and how to stop.

          Cancelling lives here, one tap, no email to anybody -- New York
          requires an online cancellation at least as easy as signing up, and
          burying it would be shabby regardless. The price and the renewal
          date are stated next to the button rather than somewhere else. */}
      {b && b.standing !== 'free' && (
        <Card>
          <SectionHead title="Subscription" />
          {b.standing === 'trial' && (
            <>
              <span className="sub">
                Your free month is running. {b.daysLeft === 0
                  ? 'It ends today.'
                  : b.daysLeft + (b.daysLeft === 1 ? ' day left.' : ' days left.')}
                {' '}After that it is {b.price} a month to post, message and apply.
                Looking around stays free.
              </span>
              <span className="hint">Ends {until(b.trialEndsAt)}. Nothing is charged automatically — you will be asked first.</span>
              {/* From day one, not day thirty. Somebody who has decided
                  should never be told to come back in a month. */}
              <button
                type="button"
                className="btn block"
                onClick={() => void startSubscription()}
              >
                Subscribe now — {b.price} a month
              </button>
              <span className="hint">
                Your free days are not lost: paying now starts the month after they run out.
              </span>
            </>
          )}
          {/* Free months from a promo or a referral. Everything works, nothing
              renews, and there is nothing to cancel -- so no Cancel button and
              no talk of a monthly price, which would be a threat rather than
              information. What it does say is when it runs out, and what
              happens then, because that is the part worth knowing early. */}
          {b.standing === 'paid' && !b.viaStripe && (
            <>
              <span className="sub">
                Free until {until(b.paidUntil)}. Nothing to pay and nothing to cancel.
              </span>
              <span className="hint">
                After that it is {b.price} a month to post, message and apply — or carry
                on browsing for free. You will not be charged without asking.
              </span>
              <button
                type="button"
                className="btn ghost block"
                onClick={() => void startSubscription()}
              >
                Set up a subscription for afterwards
              </button>
            </>
          )}
          {b.standing === 'paid' && b.viaStripe && !b.cancelled && (
            <>
              <span className="sub">
                {b.price} a month. Renews on {until(b.paidUntil)}.
              </span>
              <button
                type="button"
                className="btn quiet block"
                onClick={() => void cancelSubscription()}
              >
                Cancel my subscription
              </button>
              <span className="hint">
                You keep everything until {until(b.paidUntil)}. No part-month is charged after that.
              </span>
            </>
          )}
          {b.standing === 'paid' && b.viaStripe && b.cancelled && (
            <>
              <span className="sub">
                Cancelled. You keep everything until {until(b.paidUntil)}, then Ozeret
                goes back to looking around only.
              </span>
              <button
                type="button"
                className="btn ghost block"
                onClick={() => void resumeSubscription()}
              >
                Keep my subscription after all
              </button>
            </>
          )}
          {b.standing === 'browsing' && (
            <>
              <span className="sub">
                Looking around is free. Posting, messaging and applying are {b.price} a month.
              </span>
              <button
                type="button"
                className="btn block"
                onClick={() => void startSubscription()}
              >
                Subscribe — {b.price} a month
              </button>
            </>
          )}
        </Card>
      )}

      {/* Codes: one to type in, one to hand out. */}
      <PromoAndSharing />

      {/* The two documents, where somebody looking for them would look. */}
      <Card className="flat">
        <SectionHead title={t('tx_small_print')} />
        <span className="hint">
          {t('tx_small_print_hint')}
        </span>
        <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
          <a className="btn quiet sm" href="/terms" target="_blank" rel="noreferrer">Terms</a>
          <a className="btn quiet sm" href="/privacy" target="_blank" rel="noreferrer">Privacy</a>
        </div>
      </Card>

      {/* First thing, for the one or two people who have it. It was at the
          bottom under sign-out, which is where things go to be missed.
          The server checks again on every call -- a hidden button was never
          the permission. */}
      {state.me?.isAdmin && (
        <Card className="tint">
          <SectionHead title={t('t_admin')} />
          <span className="sub">
            How Ozeret is going: both sides of the market, what is going
            unanswered, and what people are telling you.
          </span>
          <button type="button" className="btn block" onClick={() => nav('admin')}>
            Open the dashboard
          </button>

          {/* Half of Ozeret is invisible to the person who built it. This is
              a profile of your own, not somebody else's account -- reading a
              real cleaner's messages to check a layout is not something worth
              making easy. */}
          <button
            type="button"
            className="btn ghost block"
            onClick={() => viewAs(isCleaner ? 'house' : 'cleaner')}
          >
            {isCleaner ? 'Back to your home' : 'Look at it as a cleaner'}
          </button>
          <span className="hint">
            {isCleaner
              ? 'You are seeing the cleaner side. Your home is untouched.'
              : 'Switches you to the cleaner side with a profile of your own. Switch back whenever.'}
          </span>

          <Divider />

          {/* Half of Ozeret is only ever met on a phone. Checking that from a
              laptop should not mean picking one up. */}
          <span className="hint" style={{ fontWeight: 700, color: 'var(--ink-2)' }}>
            Draw it as
          </span>
          <Seg
            options={[
              { id: 'auto', label: 'This window' },
              { id: 'mobile', label: 'Phone' },
              { id: 'desktop', label: 'Desktop' },
            ]}
            value={state.viewport}
            onChange={(v) => setViewport(v)}
          />
          <span className="hint">
            {state.viewport === 'auto'
              ? 'Following your window, as it does for everybody else.'
              : state.viewport === 'mobile'
                ? 'Pinned to the phone layout, in a phone-sized frame.'
                : 'Pinned to the desktop layout, however narrow the window gets.'}
          </span>
        </Card>
      )}

      <Card>
        <SectionHead title={t('h_language')} />
        <div className="chips">
          {UI_LANGS.map((l) => (
            <Pick
              key={l.id}
              label={l.native}
              on={state.lang === l.id}
              onClick={() => setLang(l.id)}
            />
          ))}
        </div>
        <span className="hint">
          {t('tx_lang_hint')}
        </span>
      </Card>


      {isCleaner && av && (
        <Card>
          <SectionHead title={t('h_beingfound')} />
          <div className="switchrow">
            <div className="col">
              <b style={{ fontSize: 14 }}>{t('c_showme')}</b>
              <span className="hint">
                {t('st_turnoff')}
              </span>
            </div>
            <Switch on={!av.hidden} onClick={toggleHidden} />
          </div>
        </Card>
      )}

      <HiddenList />

      {/* Your way back in that depends on nobody.

          I hid this for phone accounts, on the reasoning that a second phone
          just asks for a code. That reasoning only holds once texts are
          actually being sent -- until then the code has to be read out of the
          database by an admin, and Oster locked himself out of his own app
          within the hour. A permanent code you can write down is the door
          that is always open, so it is shown to everybody. */}
      {!state.me?.isDemo && (
        <Card>
          <SH title={t('h_otherdevice')} />
          <SigninCode />
        </Card>
      )}

      <Card>
        <SH title={t('h_account')} />
        <div className="row between"><span className="sub">{t('st_signedin')}</span><b>{state.me?.name}</b></div>
        {state.me?.phone && (
          <div className="row between"><span className="sub">Phone</span><b>{state.me.phone}</b></div>
        )}
        <Note>
          {t('tx_never_ssn')}
        </Note>
      </Card>

      <button type="button" className="btn quiet block" onClick={() => nav('feedback')}>
        {t('t_feedback')}
      </button>
      <button
        type="button"
        className="btn quiet block"
        onClick={() => { state.tour = state.role; commit(); }}
      >
        {t('tour_again')}
      </button>
      <button type="button" className="btn quiet block" onClick={() => void signOut()}>
        {t('b_signout')}
      </button>

      {!state.me?.isDemo && <DeleteAccount />}
    </div>
  );
}

/* ------------------------------------------------------ people you hid

   Only shown once there is something in it. A permanently visible "Hidden
   (0)" invites people to go and find somebody to put in it. */

function HiddenList() {
  const isCleaner = state.role === 'cleaner';

  /* Not part of the bootstrap: it is read here and nowhere else. */
  useEffect(() => { void loadHidden(); }, []);

  const mine = state.hidden.filter((h) => h.byMe);
  if (!mine.length) return null;

  return (
    <Card>
      <SH title={t('h_notshown')} />
      {mine.map((h) => (
        <div className="switchrow" key={h.houseId + h.cleanerId}>
          <div className="row" style={{ gap: 9 }}>
            <Avatar p={{ initials: h.initials, hue: h.hue }} size="sm" />
            <div className="col">
              <b style={{ fontSize: 14 }}>{h.name}</b>
              <span className="hint">
                {h.pendingInvites
                  ? h.pendingInvites +
                    ' invitation' + (h.pendingInvites === 1 ? '' : 's') + ' waiting'
                  : 'Hidden ' + fmtDate(h.at.slice(0, 10), { long: true })}
              </span>
            </div>
          </div>
          <button
            type="button"
            className="btn quiet sm"
            onClick={() => unhidePerson({ houseId: h.houseId, cleanerId: h.cleanerId })}
          >
            Undo
          </button>
        </div>
      ))}
      <span className="hint">
        {isCleaner
          ? 'Undo and they come back, along with any invitation they sent you meanwhile.'
          : 'Undo and they appear in your searches again.'}
      </span>
    </Card>
  );
}

/** Your account's permanent key. Grouped in fours because people read these
 *  aloud down a phone, and an unbroken run of eight gets misheard.
 *
 *  Shown by default. It used to start masked -- "treat it like a house key" --
 *  and the reasoning was mine rather than anybody's experience. What actually
 *  happened is that Oster went looking for his code, found a row of dots and
 *  a button he did not notice, and concluded the feature had been removed.
 *  The two failures are not equal: somebody glancing at an already-unlocked
 *  phone is a smaller problem than the owner of the account being unable to
 *  get back into it. The warning below still says what the code is worth. */
export function SigninCode() {
  const [shown, setShown] = useState(true);
  const [copied, setCopied] = useState(false);
  const raw = state.me?.signinCode ?? '';
  const pretty = raw.replace(/(.{4})(?=.)/g, '$1-');

  return (
    <>
      <span className="sub">{t('st_codehow')}</span>

      <div
        className="freewin mono"
        style={{
          justifyContent: 'center', fontSize: 20, letterSpacing: '.14em',
          padding: '14px 12px', userSelect: 'all',
        }}
      >
        {shown ? pretty : '••••-••••'}
      </div>

      <div className="row" style={{ gap: 8 }}>
        <button
          type="button"
          className="btn ghost sm grow"
          onClick={() => setShown((v) => !v)}
        >
          {shown ? t('st_hidecode') : t('st_showcode')}
        </button>
        <button
          type="button"
          className="btn ghost sm grow"
          onClick={() => {
            navigator.clipboard?.writeText(raw).then(
              () => { setCopied(true); setTimeout(() => setCopied(false), 2000); },
              () => setShown(true),
            );
          }}
        >
          {copied ? t('st_copied') : t('st_copy')}
        </button>
      </div>

      <Note warm>
        <b>{t('st_housekey')}</b> {t('st_keywarn')}
      </Note>
    </>
  );
}

/* -------------------------------------------------------------- feedback */

export function FeedbackScreen() {
  const f = state.fb;
  return (
    <div className="screen-pad">
      <Note>
        This goes straight to the two people building Ozeret. Say what you actually think —
        it is more useful than being polite.
      </Note>

      <Card>
        <SectionHead title={t('h_whatabout')} />
        <div className="chips">
          {FEEDBACK_TOPICS.map((x) => (
            <Pick
              key={x.id}
              label={x.label}
              on={f.topic === x.id}
              onClick={() => { f.topic = x.id; commit(); }}
            />
          ))}
        </div>
      </Card>

      <Card>
        <SectionHead title={t('h_tellus')} />
        <textarea
          className="textarea"
          style={{ minHeight: 130 }}
          placeholder={t('st_whathappened')}
          value={f.text}
          onChange={(e) => { f.text = e.target.value; commit(); }}
        />
        <span className="hint">
          {t('st_includescreen')}
        </span>
      </Card>

      <button
        type="button"
        className="btn block lg"
        disabled={!f.topic || !f.text.trim()}
        onClick={sendFeedback}
      >
        {t('b_send')}
      </button>

      {FEEDBACK.length > 0 && (
        <Card className="flat">
          <SectionHead title={t('h_alreadysent')} action={String(FEEDBACK.length)} />
          {FEEDBACK.map((x, i) => (
            <div key={i}>
              {i > 0 && <Divider />}
              <div className="col">
                <b style={{ fontSize: 13.5 }}>
                  {FEEDBACK_TOPICS.find((y) => y.id === x.topic)?.label}
                </b>
                <span className="sub">{x.text}</span>
              </div>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
